import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { categories, goBack, navigate, showToast, friendlyError, bumpTasksVersion } from '../store.js';
import { __ } from '../i18n.js';
import { IMPACT_OPTIONS, PRIORITY_LEVELS, TIME_PRESETS } from '../lib/labels.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

export default function TaskFormScreen({ params }) {
  const task = params && params.task;
  const isEdit = Boolean(task && task.id);
  // HH-8: set only by ProjectScreen's "maak dit de eerstvolgende stap"
  // action — a one-sentence nudge, not a wizard (brief), shown only for
  // that specific create-mode handoff.
  const promotionProject = params && params.promotionProject;

  // WP-6: initializers read from `task` whenever it's present, not just in
  // edit mode — the quick-add sheet's "Meer details" hands off a
  // task-shaped prefill (title/category_id/priority) with no `id`, and
  // this generalization is what makes that carry over losslessly. Behavior
  // for real edits (task.id set) and a blank "+" open (task undefined) is
  // unchanged: a real task always has these fields, and `task?.x` on
  // undefined is the same `undefined` the old `isEdit &&` guard produced.
  const [title, setTitle] = useState(task?.title || '');
  const [categoryId, setCategoryId] = useState(() => {
    if (task?.category_id) return task.category_id;
    return categories.value[0] ? categories.value[0].id : '';
  });
  const [description, setDescription] = useState(task?.description || '');
  const [effortMinutes, setEffortMinutes] = useState(task?.effort_minutes || 30);
  const [impact, setImpact] = useState(task?.impact || 2);
  const [priority, setPriority] = useState(task?.priority || 2);
  const [reward, setReward] = useState(task?.reward || '');
  const [deadline, setDeadline] = useState(task?.deadline || '');
  const [deadlineType, setDeadlineType] = useState(task?.deadline_type || 'soft');
  // HH-8: '' is the "geen project" sentinel for the <select> below, matching
  // categoryId's own convention of carrying the raw select value as state.
  const [projectId, setProjectId] = useState(task?.project_id || '');
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.projects
      .list()
      .then((res) => {
        if (!cancelled) setProjects(res.projects || []);
      })
      .catch(() => {
        // Fail-soft: without this the form still works, just without the
        // optional project field — same posture as ListDetailScreen's own
        // history fetch.
      })
      .finally(() => {
        if (!cancelled) setProjectsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // WP-7 (brief 7d): after 7a's self-heal this should be unreachable, but a
  // task form with a broken/empty category dropdown and no visible reason
  // must never recur — say so and offer the fix directly instead.
  if (categories.value.length === 0) {
    return (
      <div>
        <BackButton onClick={goBack} />
        <div class="etm-empty-state">{__('Je hebt nog geen categorieën.', 'etm')}</div>
        <button
          type="button"
          class="etm-btn etm-btn-primary"
          onClick={() => navigate('category', { manage: true })}
        >
          {__('Categorie aanmaken', 'etm')}
        </button>
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      showToast(__('Geef de taak een titel.', 'etm'), 'error');
      return;
    }
    if (!categoryId) {
      showToast(__('Kies een categorie.', 'etm'), 'error');
      return;
    }

    const payload = {
      title: trimmedTitle,
      category_id: Number(categoryId),
      description,
      effort_minutes: Number(effortMinutes) || 30,
      impact: Number(impact),
      priority: Number(priority),
      reward,
    };
    if (isEdit) {
      // PATCH is a partial update — an omitted field means "leave
      // unchanged", so clearing an existing deadline requires explicitly
      // sending empty values, not leaving the keys out.
      payload.deadline = deadline;
      payload.deadline_type = deadline ? deadlineType : '';
      // Same explicit-clear convention for HH-8's project_id.
      payload.project_id = projectId || '';
    } else {
      if (deadline) {
        payload.deadline = deadline;
        payload.deadline_type = deadlineType;
      }
      if (projectId) {
        payload.project_id = Number(projectId);
      }
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.tasks.update(task.id, payload);
        showToast(__('Taak opgeslagen.', 'etm'), 'success');
      } else {
        await api.tasks.create(payload);
        showToast(__('Taak toegevoegd.', 'etm'), 'success');
      }
      bumpTasksVersion(); // WP-2: keep the desktop Aankomend panel in sync
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(__('Taak verwijderen?', 'etm'))) return;
    try {
      await api.tasks.remove(task.id);
      showToast(__('Taak verwijderd.', 'etm'), 'success');
      bumpTasksVersion(); // WP-2: keep the desktop Aankomend panel in sync
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  // Only meaningful once the fetch has actually resolved — before that,
  // `projects` is just still empty, not proof the id is inaccessible.
  const hasOrphanProject = projectsLoaded && !!task?.project_id && !projects.some((p) => p.id === task.project_id);

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">{isEdit ? __('Taak bewerken', 'etm') : __('Nieuwe taak', 'etm')}</h1>

      <form onSubmit={handleSubmit}>
        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-task-title">
            {__('Titel', 'etm')}
          </label>
          <input
            id="etm-task-title"
            class="etm-form-input"
            type="text"
            value={title}
            onInput={(e) => setTitle(e.target.value)}
            placeholder={__('Wat moet er gebeuren?', 'etm')}
            autofocus
            required
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-task-category">
            {__('Categorie', 'etm')}
          </label>
          <select
            id="etm-task-category"
            class="etm-form-select"
            value={categoryId}
            onChange={(e) => setCategoryId(Number(e.target.value))}
          >
            {/* <option> can only contain text, so no category icon here —
                since WP-1 the stored value is an icon *name*, which would
                render as raw text like "home Huis". */}
            {categories.value.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
                {cat.is_shared ? __(' · gedeeld', 'etm') : ''}
              </option>
            ))}
          </select>
        </div>

        {promotionProject ? (
          <p class="etm-field-hint">
            {__('Dit wordt de eerstvolgende stap voor "%s" — geef een realistische tijdsinschatting en prioriteit.', 'etm').replace(
              '%s',
              promotionProject.title
            )}
          </p>
        ) : null}

        {/* Quick fields (title/category/priority/geschatte tijd) sit on
            top; the rest follows behind "Meer opties" — one form, two
            densities, so the quick-add sheet's "Meer details" handoff
            lands on exactly the same fields it already collected. */}
        <div class="etm-form-group">
          <label class="etm-form-label">{__('Prioriteit', 'etm')}</label>
          <div class="etm-priority-selector">
            {PRIORITY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                class={`etm-priority-btn${Number(priority) === level.value ? ' active' : ''}`}
                onClick={() => setPriority(level.value)}
              >
                {level.label()}
              </button>
            ))}
          </div>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Geschatte tijd', 'etm')}</label>
          <div class="etm-priority-selector">
            {TIME_PRESETS.map((preset) => (
              <button
                key={preset.minutes}
                type="button"
                class={`etm-priority-btn${Number(effortMinutes) === preset.minutes ? ' active' : ''}`}
                onClick={() => setEffortMinutes(preset.minutes)}
              >
                {preset.sublabel()}
              </button>
            ))}
          </div>
          <input
            id="etm-task-effort"
            class="etm-form-input etm-effort-free-input"
            type="number"
            min="1"
            value={effortMinutes}
            onInput={(e) => setEffortMinutes(e.target.value)}
            aria-label={__('Of vul zelf minuten in', 'etm')}
            placeholder={__('Of vul zelf minuten in', 'etm')}
          />
        </div>

        <details class="etm-collapsible">
          <summary>
            {__('Meer opties', 'etm')}
            <Icon name="keyboard_arrow_down" size={18} />
          </summary>

          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-task-description">
              {__('Omschrijving', 'etm')}
            </label>
            <textarea
              id="etm-task-description"
              class="etm-form-textarea"
              value={description}
              onInput={(e) => setDescription(e.target.value)}
              placeholder={__('Extra details (optioneel)', 'etm')}
            />
          </div>

          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-task-impact">
              {__('Impact', 'etm')}
            </label>
            <select
              id="etm-task-impact"
              class="etm-form-select"
              value={impact}
              onChange={(e) => setImpact(Number(e.target.value))}
            >
              {IMPACT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label()}
                </option>
              ))}
            </select>
          </div>

          {projects.length > 0 || hasOrphanProject ? (
            <div class="etm-form-group">
              <label class="etm-form-label" for="etm-task-project">
                {__('Project', 'etm')}
              </label>
              <select
                id="etm-task-project"
                class="etm-form-select"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">{__('Geen project', 'etm')}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
                {hasOrphanProject ? (
                  // Review finding: a shared task's project_id can point at a
                  // project the CURRENT viewer can't see (e.g. a personal
                  // project of whoever set it) — without this, the <select>
                  // silently falls back to displaying "Geen project" even
                  // though a real project is still assigned (the value is
                  // preserved on save either way; this is purely so the UI
                  // doesn't misrepresent that fact).
                  <option value={task.project_id}>{__('Project (geen toegang)', 'etm')}</option>
                ) : null}
              </select>
            </div>
          ) : null}

          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-task-reward">
              {__('Beloning', 'etm')}
            </label>
            <input
              id="etm-task-reward"
              class="etm-form-input"
              type="text"
              value={reward}
              onInput={(e) => setReward(e.target.value)}
              placeholder={__('bijv. daarna espresso', 'etm')}
            />
          </div>

          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-task-deadline">
              {__('Deadline', 'etm')}
            </label>
            <input
              id="etm-task-deadline"
              class="etm-form-input"
              type="date"
              value={deadline}
              onInput={(e) => setDeadline(e.target.value)}
            />
          </div>

          {deadline ? (
            <div class="etm-form-group">
              <label class="etm-form-label">{__('Soort deadline', 'etm')}</label>
              <div class="etm-toggle-row">
                <button
                  type="button"
                  class={deadlineType === 'soft' ? 'active' : ''}
                  onClick={() => setDeadlineType('soft')}
                >
                  {__('Streefdatum', 'etm')}
                </button>
                <button
                  type="button"
                  class={deadlineType === 'hard' ? 'active' : ''}
                  onClick={() => setDeadlineType('hard')}
                >
                  {__('Harde deadline', 'etm')}
                </button>
              </div>
            </div>
          ) : null}
        </details>

        <button type="submit" class="etm-btn etm-btn-primary" disabled={saving}>
          {isEdit ? __('Opslaan', 'etm') : __('Taak toevoegen', 'etm')}
        </button>
      </form>

      {isEdit ? (
        <button type="button" class="etm-btn etm-btn-danger" onClick={handleDelete}>
          {__('Verwijderen', 'etm')}
        </button>
      ) : null}
    </div>
  );
}
